import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar as CalendarIcon, Clock, ArrowLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isBefore, startOfToday } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import { Calendar } from '@/components/ui/calendar';

const BookingPage = () => {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const [venue, setVenue] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hourlyPricingRules, setHourlyPricingRules] = useState([]);
  const [bookingData, setBookingData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_address: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadVenue();
    loadPricingSettings();
  }, [venueId]);

  useEffect(() => {
    if (venue) {
      loadSlots();
    }
  }, [selectedDate, venue]);

  const loadVenue = async () => {
    try {
      const response = await api.get(`/venues/${venueId}`);
      setVenue(response.data);
    } catch (error) {
      toast.error('Gagal memuat data lapangan');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const loadSlots = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await api.get(`/bookings/slots?venue_id=${venueId}&date=${dateStr}`);
      setSlots(response.data);
    } catch (error) {
      toast.error('Gagal memuat slot waktu');
    }
  };

  const loadPricingSettings = async () => {
    try {
      const response = await api.get('/settings/public');
      const rawRulesMap = response.data?.venue_hourly_pricing_rules;
      const rawRules = response.data?.hourly_pricing_rules;
      let rulesForVenue = null;
      if (rawRulesMap) {
        try {
          const parsedMap = JSON.parse(rawRulesMap);
          if (parsedMap && typeof parsedMap === 'object') {
            const list = parsedMap?.[venueId];
            if (Array.isArray(list)) rulesForVenue = list;
          }
        } catch {}
      }

      if (rulesForVenue) {
        setHourlyPricingRules(rulesForVenue);
      } else if (rawRules) {
        const parsed = JSON.parse(rawRules);
        setHourlyPricingRules(Array.isArray(parsed) ? parsed : []);
      } else {
        setHourlyPricingRules([]);
      }
    } catch {
      setHourlyPricingRules([]);
    }
  };

  const getDayType = (dateStr) => {
    const dt = dateStr ? new Date(`${dateStr}T00:00:00`) : null;
    if (!dt || Number.isNaN(dt.getTime())) return 'weekday';
    const day = dt.getDay();
    return day === 0 || day === 6 ? 'weekend' : 'weekday';
  };

  const getHourlyRate = (hour, dayType) => {
    const rules = Array.isArray(hourlyPricingRules) ? hourlyPricingRules : [];
    const day = String(dayType || 'all').toLowerCase();

    for (const rule of rules) {
      const startHour = Number(rule?.start_hour ?? rule?.startHour);
      const endHour = Number(rule?.end_hour ?? rule?.endHour);
      const pricePerHour = Number(rule?.price_per_hour ?? rule?.pricePerHour);
      const ruleDayType = String(rule?.day_type ?? rule?.dayType ?? 'all').toLowerCase();
      if (!Number.isInteger(startHour) || !Number.isInteger(endHour)) continue;
      if (!Number.isFinite(pricePerHour) || pricePerHour <= 0) continue;
      if (ruleDayType === day && hour >= startHour && hour < endHour) return pricePerHour;
    }

    for (const rule of rules) {
      const startHour = Number(rule?.start_hour ?? rule?.startHour);
      const endHour = Number(rule?.end_hour ?? rule?.endHour);
      const pricePerHour = Number(rule?.price_per_hour ?? rule?.pricePerHour);
      const ruleDayType = String(rule?.day_type ?? rule?.dayType ?? 'all').toLowerCase();
      if (!Number.isInteger(startHour) || !Number.isInteger(endHour)) continue;
      if (!Number.isFinite(pricePerHour) || pricePerHour <= 0) continue;
      if (ruleDayType === 'all' && hour >= startHour && hour < endHour) return pricePerHour;
    }

    return null;
  };

  const calculateTotalAmount = (timeSlots, bookingDate) => {
    const slotsArr = Array.isArray(timeSlots) ? timeSlots : [];
    if (slotsArr.length === 0) return 0;
    const dayType = getDayType(bookingDate);
    let total = 0;
    for (const slot of slotsArr) {
      const match = typeof slot === 'string' ? slot.match(/^(\d{1,2}):/) : null;
      const hour = match ? Number(match[1]) : null;
      if (!Number.isInteger(hour)) return null;
      const rate = getHourlyRate(hour, dayType);
      if (!rate) return null;
      total += rate;
    }
    return total;
  };

  const handleBooking = async () => {
    if (selectedSlots.length === 0) {
      toast.error('Pilih minimal 1 slot waktu');
      return;
    }

    const bookingDateStr = format(selectedDate, 'yyyy-MM-dd');
    const totalAmount = calculateTotalAmount(selectedSlots, bookingDateStr);
    if (totalAmount == null || totalAmount <= 0) {
      toast.error('Harga belum diatur untuk jam yang dipilih. Silakan atur di Harga.');
      return;
    }

    if (!bookingData.customer_name || !bookingData.customer_phone || !bookingData.customer_address) {
      toast.error('Lengkapi semua data');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/bookings', {
        venue_id: venueId,
        booking_date: bookingDateStr,
        time_slots: selectedSlots,
        ...bookingData
      });
      toast.success('Booking berhasil! Silakan lanjutkan pembayaran');
      navigate(`/payment/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking gagal');
    } finally {
      setSubmitting(false);
    }
  };

  const dates = Array.from({ length: 7 }, (_, i) => addDays(new Date(), i));

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlots([]);
    setShowCalendar(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3">
              <img src="/zero-logo.png" alt="Logo" className="w-10 h-10 object-contain rounded-md" />
              <span className="text-2xl font-heading font-black italic text-primary uppercase">Zero Futsal</span>
            </Link>
            <Link to="/" className="text-zinc-400 hover:text-primary flex items-center gap-2">
              <ArrowLeft className="w-5 h-5" />
              <span>Kembali</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Venue Info */}
          <div className="glass glow-border rounded-3xl p-6 mb-8">
            <h1 className="text-4xl md:text-5xl font-heading font-black uppercase text-white mb-2">{venue?.name}</h1>
            <div className="flex items-center flex-wrap gap-3">
              <span className="inline-block bg-white/5 text-white/80 border border-white/10 px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                {venue?.type}
              </span>
              {(() => {
                const bookingDateStr = format(selectedDate, 'yyyy-MM-dd');
                const dayType = getDayType(bookingDateStr);
                const openHour = Number(venue?.open_hour ?? 8);
                const closeHour = Number(venue?.close_hour ?? 22);
                const hours = [];
                for (let h = openHour; h < closeHour; h++) hours.push(h);
                const rates = hours.map((h) => getHourlyRate(h, dayType)).filter((x) => typeof x === 'number');
                if (rates.length === 0) {
                  return <span className="text-sm text-white/60">Harga belum diatur</span>;
                }
                const min = Math.min(...rates);
                const max = Math.max(...rates);
                return (
                  <span className="text-xl font-black text-primary glow-text">
                    {min === max ? `Rp ${min.toLocaleString('id-ID')}/jam` : `Mulai Rp ${min.toLocaleString('id-ID')}/jam`}
                  </span>
                );
              })()}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Booking Form */}
            <div className="lg:col-span-2 space-y-8">
              {/* Date Selection */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <CalendarIcon className="w-6 h-6 text-primary" />
                    <h2 className="text-xl font-bold text-white uppercase font-heading">Pilih Tanggal</h2>
                  </div>
                  <button
                    onClick={() => setShowCalendar(!showCalendar)}
                    className="text-primary hover:text-primary-hover font-semibold text-sm flex items-center gap-2"
                  >
                    {showCalendar ? 'Tutup Kalender' : 'Buka Kalender'}
                    <CalendarIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Quick dates */}
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-3 mb-4">
                  {dates.map((date) => {
                    const isSelected = format(date, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
                    return (
                      <button
                        key={date.toString()}
                        data-testid={`date-${format(date, 'yyyy-MM-dd')}`}
                        onClick={() => handleDateSelect(date)}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          isSelected
                            ? 'bg-primary border-primary text-black'
                            : 'bg-zinc-800 border-zinc-700 text-white hover:border-primary/50'
                        }`}
                      >
                        <div className="text-xs font-semibold">
                          {format(date, 'EEE', { locale: idLocale })}
                        </div>
                        <div className="text-lg font-bold">{format(date, 'd')}</div>
                        <div className="text-xs">{format(date, 'MMM', { locale: idLocale })}</div>
                      </button>
                    );
                  })}
                </div>

                {/* Calendar picker */}
                {showCalendar && (
                  <div className="border-t border-zinc-700 pt-4">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateSelect}
                      disabled={(date) => isBefore(date, startOfToday())}
                      className="rounded-md border border-zinc-700"
                      classNames={{
                        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                        month: "space-y-4 w-full",
                        caption: "flex justify-center pt-1 relative items-center text-white",
                        caption_label: "text-sm font-medium",
                        nav: "space-x-1 flex items-center",
                        nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-white",
                        table: "w-full border-collapse space-y-1",
                        head_row: "flex",
                        head_cell: "text-zinc-400 rounded-md w-9 font-normal text-[0.8rem]",
                        row: "flex w-full mt-2",
                        cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-zinc-800 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                        day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 text-white hover:bg-zinc-800 rounded-md",
                        day_selected: "bg-primary text-black hover:bg-primary hover:text-black focus:bg-primary focus:text-black",
                        day_today: "bg-zinc-800 text-primary",
                        day_outside: "text-zinc-600 opacity-50",
                        day_disabled: "text-zinc-700 opacity-50",
                        day_hidden: "invisible",
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Time Slot Selection */}
              <div className="glass glow-border rounded-3xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Clock className="w-6 h-6 text-primary" />
                    <div>
                      <h2 className="text-xl font-bold text-white uppercase font-heading">Pilih Jam</h2>
                      <p className="text-sm text-zinc-400">Bisa pilih lebih dari 1 jam</p>
                    </div>
                  </div>
                  {selectedSlots.length > 0 && (
                    <div className="bg-primary/15 border border-primary/30 px-4 py-1 rounded-full">
                      <span className="text-primary font-bold">{selectedSlots.length} jam</span>
                    </div>
                  )}
                </div>
                <div className="slot-grid">
                  {slots.map((slot) => {
                    const isSelected = selectedSlots.includes(slot.time);
                    return (
                      <button
                        key={slot.time}
                        data-testid={`slot-${slot.time}`}
                        onClick={() => {
                          if (slot.available) {
                            if (isSelected) {
                              setSelectedSlots(selectedSlots.filter(s => s !== slot.time));
                            } else {
                              setSelectedSlots([...selectedSlots, slot.time]);
                            }
                          }
                        }}
                        disabled={!slot.available}
                        className={`p-4 rounded-2xl border font-bold transition-all relative ${
                          !slot.available
                            ? 'bg-white/5 border-white/10 text-white/30 cursor-not-allowed'
                            : isSelected
                            ? 'bg-primary border-primary text-black shadow-[0_0_0_1px_rgba(206,255,5,0.45),0_0_32px_rgba(206,255,5,0.16)]'
                            : 'bg-black/30 border-white/10 text-white hover:border-primary/50 hover:shadow-[0_0_0_1px_rgba(206,255,5,0.25),0_0_24px_rgba(206,255,5,0.10)]'
                        }`}
                      >
                        <div className="text-sm">{slot.time}</div>
                        {!slot.available && (
                          <div className="text-xs mt-1 text-white/40">Penuh</div>
                        )}
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-black rounded-full flex items-center justify-center border border-white/10">
                            <span className="text-primary text-xs">✓</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Customer Info Form */}
              <div className="glass glow-border rounded-3xl p-6">
                <h2 className="text-xl font-bold text-white uppercase font-heading mb-6">Data Pemesan</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-2">Nama Lengkap</label>
                    <input
                      data-testid="customer-name-input"
                      type="text"
                      required
                      value={bookingData.customer_name}
                      onChange={(e) => setBookingData({ ...bookingData, customer_name: e.target.value })}
                      className="w-full bg-black/30 border border-white/10 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-2xl h-12 px-4 placeholder:text-white/50 transition-all outline-none"
                      placeholder="Nama lengkap Anda"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-2">No. Handphone</label>
                    <input
                      data-testid="customer-phone-input"
                      type="tel"
                      required
                      value={bookingData.customer_phone}
                      onChange={(e) => setBookingData({ ...bookingData, customer_phone: e.target.value })}
                      className="w-full bg-black/30 border border-white/10 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-2xl h-12 px-4 placeholder:text-white/50 transition-all outline-none"
                      placeholder="08123456789"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-2">Alamat</label>
                    <textarea
                      data-testid="customer-address-input"
                      required
                      value={bookingData.customer_address}
                      onChange={(e) => setBookingData({ ...bookingData, customer_address: e.target.value })}
                      className="w-full bg-black/30 border border-white/10 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-2xl px-4 py-3 placeholder:text-white/50 transition-all outline-none resize-none"
                      placeholder="Alamat lengkap Anda"
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="glass glow-border rounded-3xl p-6 sticky top-24">
                <h2 className="text-xl font-bold text-white uppercase font-heading mb-6">Ringkasan Booking</h2>
                <div className="space-y-4 mb-6">
                  <div>
                    <div className="text-sm text-zinc-400">Lapangan</div>
                    <div className="text-white font-bold">{venue?.name}</div>
                  </div>
                  <div>
                    <div className="text-sm text-zinc-400">Tanggal</div>
                    <div className="text-white font-bold">
                      {format(selectedDate, 'dd MMMM yyyy', { locale: idLocale })}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-zinc-400">Jam Dipilih</div>
                    <div className="text-white font-bold">
                      {selectedSlots.length === 0 ? '-' : 
                        selectedSlots.length === 1 ? selectedSlots[0] :
                        `${selectedSlots.length} slot`
                      }
                    </div>
                    {selectedSlots.length > 1 && (
                      <div className="text-xs text-zinc-500 mt-1">
                        {selectedSlots.sort().join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="border-t border-white/10 pt-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="text-sm text-zinc-400">Jumlah jam</div>
                      <div className="text-white font-bold">
                        {selectedSlots.length} jam
                      </div>
                    </div>
                    <div className="text-sm text-zinc-400 mb-1">Total Harga</div>
                    {(() => {
                      const bookingDateStr = format(selectedDate, 'yyyy-MM-dd');
                      const totalAmount = calculateTotalAmount(selectedSlots, bookingDateStr);
                      if (selectedSlots.length === 0) return <div className="text-3xl font-black text-white/40">Rp -</div>;
                      if (totalAmount == null || totalAmount <= 0) return <div className="text-3xl font-black text-white/40">Rp -</div>;
                      return (
                        <div className="text-3xl font-black text-primary glow-text">
                          Rp {totalAmount.toLocaleString('id-ID')}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <button
                  data-testid="submit-booking-btn"
                  onClick={handleBooking}
                  disabled={(() => {
                    const bookingDateStr = format(selectedDate, 'yyyy-MM-dd');
                    const totalAmount = calculateTotalAmount(selectedSlots, bookingDateStr);
                    return submitting || selectedSlots.length === 0 || totalAmount == null || totalAmount <= 0;
                  })()}
                  className="w-full bg-primary text-black hover:bg-primary-hover rounded-full px-8 py-4 font-bold uppercase tracking-wider transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-[0_0_0_1px_rgba(206,255,5,0.45),0_0_36px_rgba(206,255,5,0.12)]"
                >
                  <span>{submitting ? 'Memproses...' : 'Lanjut Pembayaran'}</span>
                  {!submitting && <ChevronRight className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BookingPage;
